import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { session } from '@/shared/session/session';

import { toInvitation } from '../api/mappers';
import { useInviteMember } from '../hooks/use-member-mutations';
import { useRoles } from '../hooks/use-roles';
import {
  inviteMemberSchema,
  type InviteMemberInput,
  type InviteMemberOutput,
} from '../schemas/member-form-schema';
import { assignableRoles, inviteErrorTarget } from '../schemas/member-rules';

import { MemberAccessFields } from './member-access-fields';

const DEFAULTS: InviteMemberInput = {
  email: '',
  name: '',
  roleKey: '',
  access: 'all',
  storeIds: [],
};
const KNOWN_FIELDS: ReadonlySet<string> = new Set(['email', 'name', 'roleKey', 'storeIds']);

/** Sends an invitation email; the new row shows as invited until the link is used. */
/** `tour` marks the header instance the team tour points at; the empty state has the other. */
export function InviteMemberDialog({ tour = false }: { tour?: boolean }) {
  const { t } = useTranslation(['team', 'common']);
  const { me } = session.useSession();
  const roles = useRoles();
  const invite = useInviteMember();
  const message = useErrorMessage();
  const [open, setOpen] = useState(false);
  const form = useForm<InviteMemberInput, unknown, InviteMemberOutput>({
    resolver: useI18nResolver(inviteMemberSchema),
    defaultValues: DEFAULTS,
  });

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      form.reset(DEFAULTS);
      invite.reset();
    }
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await invite.mutateAsync(toInvitation(values));
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const target = inviteErrorTarget(error);
      if (target) {
        form.setError(target.field, { type: 'server', message: t(target.key, target.values) });
      } else {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-tour={tour ? 'org.team.invite' : undefined}>
          <Plus aria-hidden />
          {t('team:invite.action')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('team:invite.title')}</DialogTitle>
          <DialogDescription>{t('team:invite.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('team:fields.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="off"
                      placeholder={t('team:fields.emailPlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('team:fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <MemberAccessFields
              roles={assignableRoles(roles.data ?? [], me?.role.key ?? '', me?.permissions)}
              stores={me?.stores ?? []}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? t('team:invite.submitting') : t('team:invite.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
